import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import Select from 'react-select';
import MultiLanguageInput from './MultiLanguageInput';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import { tr } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css'; 
import './AdminModals.css';
registerLocale('tr', tr);
const API_URL = process.env.REACT_APP_API_URL;

// react-select menüsünün modalın üzerinde görünmesini sağlayan stil objesi
const customSelectStylesForModal = {
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const BannerFormModal = ({ isOpen, onClose, onSave, banner }) => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const isEditMode = !!banner;

  const [formData, setFormData] = useState({
    title: '', content: {}, button_text: {}, button_link: '',
    target_languages: [], start_date: '', end_date: '', is_active: true,
  });
  
  const [dbLanguages, setDbLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [selectedContentLangs, setSelectedContentLangs] = useState([]);
  const [selectedButtonLangs, setSelectedButtonLangs] = useState([]);

  const languageOptionsForSelect = useMemo(() => {
    // 1. Öncelik verilecek dil kodlarını bir diziye yazıyoruz
    const priorityCodes = ['tr', 'en', 'de', 'fr'];

    const mappedLanguages = dbLanguages.map(lang => ({
        value: lang.code,
        label: t(`lang_${lang.code}`, lang.name)
    }));
	
	mappedLanguages.sort((a, b) => {
        const indexA = priorityCodes.indexOf(a.value);
        const indexB = priorityCodes.indexOf(b.value);

        // Eğer her iki dil de öncelik listesindeyse, kendi sıralarına göre yerleşirler
        if (indexA > -1 && indexB > -1) {
            return indexA - indexB;
        }
        // Sadece A dili öncelik listesindeyse, en başa gelir
        if (indexA > -1) {
            return -1;
        }
        // Sadece B dili öncelik listesindeyse, en başa gelir
        if (indexB > -1) {
            return 1;
        }
        // İkisi de öncelik listesinde değilse, alfabetik olarak (A'dan Z'ye) sıralanırlar
        return a.label.localeCompare(b.label, 'tr'); // Türkçe karakterlere uygun sıralama
    });

    return mappedLanguages;

}, [dbLanguages, t]);

  useEffect(() => {
    const fetchLanguages = async () => {
        if (dbLanguages.length === 0) {
            try {
                const response = await fetch(`${API_URL}/api/public/languages`);
                if (!response.ok) throw new Error('Diller alınamadı.');
                const data = await response.json();
                setDbLanguages(data);
            } catch (err) { console.error(err); }
        }
    };
    if (isOpen) {
        fetchLanguages();
    }
  }, [isOpen, dbLanguages.length]);
  
  useEffect(() => {
    if (isOpen) {
        if (isEditMode && banner && dbLanguages.length > 0) {
            const initialContent = banner.content || {};
            const initialButtonText = banner.button_text || {};
            setFormData({
                title: banner.title || '', content: initialContent, button_text: initialButtonText,
                button_link: banner.button_link || '', target_languages: banner.target_languages || [],
                start_date: banner.start_date ? new Date(banner.start_date).toISOString().slice(0, 10) : '',
                end_date: banner.end_date ? new Date(banner.end_date).toISOString().slice(0, 10) : '',
                is_active: banner.is_active !== undefined ? banner.is_active : true,
            });
            const langOpts = dbLanguages.map(lang => ({ value: lang.code, label: t(`lang_${lang.code}`, lang.name) }));
            setSelectedContentLangs(langOpts.filter(opt => Object.keys(initialContent).includes(opt.value)));
            setSelectedButtonLangs(langOpts.filter(opt => Object.keys(initialButtonText).includes(opt.value)));
        } else if (!isEditMode) {
            setFormData({
                title: '', content: {}, button_text: {}, button_link: '',
                target_languages: [], start_date: '', end_date: '', is_active: true
            });
            setSelectedContentLangs([]);
            setSelectedButtonLangs([]);
        }
    }
  }, [isOpen, isEditMode, banner, dbLanguages, t]);

  const handleContentLangsChange = (options) => {
    setSelectedContentLangs(options || []);
    const newContent = {};
    (options || []).forEach(opt => { newContent[opt.value] = formData.content[opt.value] || ''; });
    setFormData(prev => ({ ...prev, content: newContent }));
  };
  const handleButtonLangsChange = (options) => {
    setSelectedButtonLangs(options || []);
    const newButtonText = {};
    (options || []).forEach(opt => { newButtonText[opt.value] = formData.button_text[opt.value] || ''; });
    setFormData(prev => ({ ...prev, button_text: newButtonText }));
  };
  const handleMultiLangFieldChange = (field, newValue) => { setFormData(prev => ({ ...prev, [field]: newValue })); };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };
  const handleTargetLangChange = (selectedOptions) => { setFormData(prev => ({...prev, target_languages: selectedOptions ? selectedOptions.map(opt => opt.value) : [] })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    const url = isEditMode ? `${API_URL}/api/admin/banners/${banner.id}` : `${API_URL}/api/admin/banners`;
    const method = isEditMode ? 'PUT' : 'POST';
    try {
      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(t(data.messageKey) || (isEditMode ? t('error_banner_update') : t('error_banner_create')));
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
const handleDateChange = (date, field) => {
    // Gelen tarihi API'ye uygun format olan 'YYYY-MM-DD'ye çeviriyoruz
    const formattedDate = date ? new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().split('T')[0] : '';
    setFormData(prev => ({
      ...prev,
      [field]: formattedDate,
    }));
  };
  return ReactDOM.createPortal(
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>{isEditMode ? t('edit_banner') : t('add_new_banner')}</h3>
          <button onClick={onClose} className="close-btn">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="admin-modal-form">
            <div className="form-group-modal"><label>{t('banner_title_internal')}</label><input name="title" value={formData.title} onChange={handleChange} required /></div>
            <div className="form-group-modal">
                <label>{t('content_text')}</label>
                <MultiLanguageInput value={formData.content || {}} onChange={(newValue) => handleMultiLangFieldChange('content', newValue)} allLanguages={languageOptionsForSelect} selectedLangs={selectedContentLangs} onSelectedLangsChange={handleContentLangsChange} placeholderKey="content_placeholder" />
            </div>
            <div className="form-group-modal">
                <label>{t('button_text')} ({t('optional')})</label>
                <MultiLanguageInput value={formData.button_text || {}} onChange={(newValue) => handleMultiLangFieldChange('button_text', newValue)} allLanguages={languageOptionsForSelect} selectedLangs={selectedButtonLangs} onSelectedLangsChange={handleButtonLangsChange} placeholderKey="button_text_placeholder" />
            </div>
            <div className="form-group-modal">
    <label>{t('button_link')} ({t('optional')})</label>
    <input 
        name="button_link" 
        className="modal-text-input" /* BU SATIRI EKLEYİN */
        value={formData.button_link || ''} 
        onChange={handleChange} 
        placeholder="https://..." 
    />
</div>
            <div className="form-group-modal"><label>{t('target_languages')} ({t('optional')})</label>
                <Select 
                    isMulti 
                    options={languageOptionsForSelect} 
                    value={languageOptionsForSelect.filter(lang => formData.target_languages.includes(lang.value))} 
                    onChange={handleTargetLangChange} 
                    menuPortalTarget={document.body}
                    styles={customSelectStylesForModal}
                />
                <small>{t('target_languages_desc')}</small>
            </div>
            <div className="form-row-modal">
            <div className="form-group-modal half-width">
                <label>{t('start_date')} ({t('optional')})</label>
                <DatePicker
                    locale="tr" // Türkçe takvim
                    selected={formData.start_date ? new Date(formData.start_date) : null}
                    onChange={(date) => handleDateChange(date, 'start_date')}
                    dateFormat="dd.MM.yyyy" // Gösterim formatı
                    placeholderText={t('select_date')}
                    className="date-picker-input" // Özel stil için class
                    isClearable // Temizleme butonu ekler
                />
            </div>
            <div className="form-group-modal half-width">
                <label>{t('end_date')} ({t('optional')})</label>
                <DatePicker
                    locale="tr"
                    selected={formData.end_date ? new Date(formData.end_date) : null}
                    onChange={(date) => handleDateChange(date, 'end_date')}
                    dateFormat="dd.MM.yyyy"
                    placeholderText={t('select_date')}
                    className="date-picker-input"
                    isClearable
                    minDate={formData.start_date ? new Date(formData.start_date) : null} // Bitiş tarihi, başlangıçtan önce olamaz
                />
            </div>
        </div>
            <div className="form-group-modal checkbox-group-modal"><label className="checkbox-label-modal"><input name="is_active" type="checkbox" checked={formData.is_active} onChange={handleChange} />{t('active')}</label></div>
            {error && <p className="modal-error">{error}</p>}
            <div className="admin-modal-footer"><button type="button" onClick={onClose} className="btn-secondary">{t('cancel')}</button><button type="submit" className="btn-primary" disabled={loading}>{loading ? t('saving') : (isEditMode ? t('save_changes') : t('create_banner'))}</button></div>
        </form>
      </div>
    </div>,
    document.getElementById('modal-root')
  );
};

export default BannerFormModal;